import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";
import {
  ShoppingCartContext,
  LanguageContext,
} from "@eriksdigital/atomic-ui/components";
import { useRaffaella } from "@eriksdigital/raffaella";
import { languageToCode } from "../utils/languageUtils";

const MAX_RETRIES = 5;
const RETRY_DELAY_MS = 1000;

export const ShoppingCartProvider = ({ children }: { children: ReactNode }) => {
  const [shoppingCart, makeResourceCall] = useRaffaella("shoppingCart");
  const { language, erpSystem } = useContext(LanguageContext) as {
    language: any;
    erpSystem: any;
  };
  const retryCountRef = useRef(0);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const callApi = useCallback(() => {
    if (makeResourceCall && language && erpSystem) {
      makeResourceCall({
        apiKey: "shoppingCart",
        reqData: {
          locale: languageToCode(language),
          erpSystem: erpSystem.toUpperCase(),
          targetSystem: "SEL_ORI",
        },
      });
    }
  }, [makeResourceCall, language, erpSystem]);

  const fetchShoppingCart = useCallback(() => {
    retryCountRef.current = 0;
    callApi();
  }, [callApi]);

  useEffect(() => {
    if (shoppingCart?.error && retryCountRef.current < MAX_RETRIES) {
      retryCountRef.current += 1;
      retryTimeoutRef.current = setTimeout(callApi, RETRY_DELAY_MS);
    }

    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, [shoppingCart?.error, callApi]);

  return (
    <ShoppingCartContext.Provider value={{ fetchShoppingCart }}>
      {children}
    </ShoppingCartContext.Provider>
  );
};
